import { reactive, readonly, ref, type ComponentPublicInstance } from 'vue';
import { defineStore } from '@dh-io-owpi/shared/src/stores/store';
import { useAemData } from '../../composables/useAemData';
import { defaultSections, sectionAnchorMap, type SectionName, type SectionVisibility } from './constants';
import { createLazyLoadIO, getVisibleSections, scrollToAnchor } from './utils';

// 25% of the section height to start loading
const defaultLazyLoadRootMargin = '25% 0% 25% 0%';

/**
 * Store to control the visibility of the sections
 * It serves 3 porpuses:
 * 1. It allows to control the visibility of the sections from the AEM data
 *    by setting the `_allowedSections` and `_blockedSections` properties.
 *    This is not an actuall AEM property, but a custom one that is used on:
 *    - index.html - to control what sections we want to show/hide while developing
 *    - mount() of E2E tests - to control what sections we want to show/hide while testing
 * 2. It allows to control the visibility of the sections from the UI
 *    responding to API errors or lack of data to show
 */
export const useSectionsStore = defineStore('spa-sections', () => {
  const { _allowedSections, _blockedSections } = useAemData();
  // Get the sections that should be visible based on the AEM data
  const allowedSections = getVisibleSections(_allowedSections, _blockedSections);
  // Create a reactive object with a copy the allowed sections
  const visibleSections = reactive<SectionVisibility>({ ...allowedSections });
  const loadedSections = reactive<SectionVisibility>({ ...allowedSections });

  const activeSection = ref<SectionName>('stage');

  const currentSectionIO = new IntersectionObserver(
    (entries) => {
      const intersectingEntry = entries.find((entry) => entry.isIntersecting);
      const section = (intersectingEntry?.target as HTMLElement | undefined)?.dataset.section;

      if (section && section !== activeSection.value) {
        activeSection.value = section as SectionName;
      }
    },
    {
      rootMargin: '-50% 0% -50% 0%',
      threshold: [0, 0.25, 0.5, 0.75, 1],
    },
  );

  return reactive({
    visible: readonly(visibleSections),
    allowed: readonly(allowedSections),
    loaded: readonly(loadedSections),
    // @ts-ignore - no idea why TS is complaining about this
    active: readonly(activeSection),
    /**
     * Register a section to be observed by the IntersectionObserver
     */
    register(name: SectionName, loadingStrategy: 'lazy' | 'eager', rootMargin = defaultLazyLoadRootMargin) {
      let element: Element;
      let lazyLoadingIO: IntersectionObserver;
      loadedSections[name] = loadingStrategy === 'eager';
      const checkForActive = sectionAnchorMap[name];

      return {
        /**
         * Set the ref of the section to be observed by the IntersectionObserver
         * Pass it to the element to be observed, using the `:ref` directive
         * <div :ref="sectionRef" />
         */
        sectionRef(ref: Element | ComponentPublicInstance | null) {
          if (ref) {
            element = ref instanceof Element ? ref : ref.$el;
            if (checkForActive) currentSectionIO.observe(element);

            if (loadingStrategy === 'lazy') {
              lazyLoadingIO = createLazyLoadIO(element, rootMargin, () => {
                loadedSections[name] = true;
              });
            }
          } else if (element) {
            if (checkForActive) currentSectionIO.unobserve(element);
            lazyLoadingIO?.disconnect();
          }
        },
      };
    },
    /**
     * Scroll to the specified section
     */
    scrollIntoView(section: SectionName) {
      const loadedIndex = defaultSections.indexOf(section);

      const allSectionsLoaded = defaultSections.slice(0, loadedIndex + 1).every((section) => loadedSections[section]);
      // Set the loaded state of the sections up to the specified section
      for (let i = 0; i <= loadedIndex; i++) loadedSections[defaultSections[i]] = true;

      const urlHash = `#${sectionAnchorMap[section]}`;

      // This logic prevents the scroll to top/bottom when the user closes the wb-modal on offer teaser
      document.documentElement.style.scrollBehavior = 'smooth';
      setTimeout(() => {
        document.documentElement.style.scrollBehavior = '';
      }, 300);

      if (location.hash !== urlHash) location.hash = urlHash;
      else {
        location.hash = '';
        location.hash = urlHash;
      }
      if (!allSectionsLoaded) scrollToAnchor(urlHash);
    },
    /**
     * Set the visibility of the specified section If the section is allowed  (by default all are allowed)
     */
    setVisibility(section: SectionName, visible: boolean) {
      if (allowedSections[section]) visibleSections[section] = visible;
    },
    /**
     * Hide the specified sections
     */
    hideSections(sections: SectionName[]) {
      sections.forEach((section) => (visibleSections[section] = false));
    },
    /**
     * Show the specified sections if they are allowed (by default all are allowed)
     */
    showSections(sections: SectionName[]) {
      sections //
        .filter((section) => allowedSections[section])
        .forEach((section) => (visibleSections[section] = true));
    },
    /**
     * Show all sections that are allowed (by default all are allowed)
     */
    reset() {
      Object.assign(visibleSections, allowedSections);
    },
  });
});
